import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { has } from 'ramda';
import { TLegacyErrors } from '@alycecom/services';

import { resetError, resetShipping, validateShipping } from '../store/shipping/shipping.actions';
import { getGiftHashId } from '../store/data/data.selectors';
import {
  getErrors,
  getIsAddressChanged,
  getIsAddressVerified,
  getIsPending,
  getIsValidated,
  getShippingAddress,
  getVerifiedAddress,
} from '../store/shipping/shipping.selectors';
import { setShippingAddress } from '../store/giftFlow/giftFlow.actions';
import { getProduct } from '../store/giftFlow/giftFlow.selectors';
import {
  AddressOption,
  IShippingAddress,
  IShippingValidation,
  IVerifiedShippingValidation,
} from '../store/shipping/shipping.types';
import { transformVerifiedAddress } from '../store/shipping/shipping.helpers';
import { IVerifiedAddress } from '../types/Shipping.types';

export const useShipping = (): {
  isLoading: boolean;
  errors: TLegacyErrors;
  submitShippingAddress: (address: IShippingAddress) => void;
  resetFieldError: (fieldName: string) => void;
  selectShippingAddress: (option: AddressOption) => void;
  saveShippingAddress: () => void;
  shouldGoToTheNextStep: boolean;
} => {
  const dispatch = useDispatch();

  const hashId = useSelector(getGiftHashId);
  const isLoading = useSelector(getIsPending);
  const errors = useSelector(getErrors);
  const isValidated = useSelector(getIsValidated);
  const shippingAddress = useSelector(getShippingAddress);
  const product = useSelector(getProduct);
  const verifiedAddress = useSelector(getVerifiedAddress);
  const isAddressVerified = useSelector(getIsAddressVerified);
  const isAddressChanged = useSelector(getIsAddressChanged);

  const resetFieldError = useCallback(
    name => {
      if (has(name, errors)) {
        dispatch(resetError({ name }));
      }
    },
    [dispatch, errors],
  );

  const submitShippingAddress = useCallback(
    (address: IShippingAddress) => {
      dispatch(validateShipping.pending({ hashId, product, address }));
    },
    [dispatch, hashId, product],
  );

  const selectShippingAddress = useCallback(
    (option: AddressOption) => {
      const isOriginalAddressSelected = option === AddressOption.original;

      let selectedAddress: IVerifiedShippingValidation;
      const commonAddressPart = {
        verifiedAddress: !isOriginalAddressSelected ? verifiedAddress : null,
        isAddressVerified: !isOriginalAddressSelected && isAddressVerified,
        isAddressChanged: !isOriginalAddressSelected && isAddressChanged,
      };

      if (isOriginalAddressSelected) {
        selectedAddress = {
          address: shippingAddress?.address as IShippingAddress,
          ...commonAddressPart,
        };
      } else {
        const transformedVerifiedAddress = transformVerifiedAddress(verifiedAddress as IVerifiedAddress);
        selectedAddress = {
          address: {
            ...transformedVerifiedAddress,
            phone: shippingAddress?.address.phone as string,
            company: transformedVerifiedAddress.company || (shippingAddress?.address.company as string),
          },
          ...commonAddressPart,
        };
      }

      dispatch(setShippingAddress(selectedAddress));
      dispatch(resetShipping());
    },
    [verifiedAddress, shippingAddress, isAddressVerified, isAddressChanged, dispatch],
  );

  const saveShippingAddress = useCallback(() => {
    dispatch(
      setShippingAddress({
        ...(shippingAddress as IShippingValidation),
        verifiedAddress,
        isAddressVerified,
        isAddressChanged,
      }),
    );
    dispatch(resetShipping());
  }, [dispatch, shippingAddress, verifiedAddress, isAddressVerified, isAddressChanged]);

  useEffect(() => {
    dispatch(resetShipping());
  }, [dispatch]);

  const isAddressValidated = isValidated && !!shippingAddress;
  const shouldGoToTheNextStep = isAddressValidated && isAddressVerified && !isAddressChanged;

  return {
    isLoading,
    errors,
    submitShippingAddress,
    resetFieldError,
    selectShippingAddress,
    saveShippingAddress,
    shouldGoToTheNextStep,
  };
};
